import React, { useEffect, useRef } from "react";
import {
  ArrayInput,
  Create,
  DateTimeInput,
  DateInput,
  Edit,
  Labeled,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useCreateController,
  useEditController,
  useTranslate,
  Show,
  SimpleShowLayout,
  SelectField,
  ArrayField,
  Datagrid,
  NumberField,
  DateField,
  useShowController,
  maxLength,
  FunctionField,
} from "react-admin";
import Box from "@mui/material/Box";

import { AttachmentFileField, AttachmentFileInput } from "../components/AttachmentFile";
import {
  createDeliveryStatusChoices,
  createDeliveryTypeChoices,
  createHacobellPreferredVehicleChoices,
  createHacobellStatuses,
  createHasProducerReviewed,
  createIsExceptionFlow,
} from "../components/choices";
import { DelivererForm } from "../components/DeliveryForm";
import { IndividualDeliveryToolbar } from "../components/IndividualToolbar";
import { UndeletableToolbar } from "../components/UndeletableToolbar";

import { Grade } from "../api/common";
import { InspectionResult } from "../api/amend";
import { useSpotContractAmend } from "../hooks/useAmend";
import { useSpotContractUnassignedDeliveryContents } from "../hooks/useUnassignedDeliveryContents";
import { validateRequired } from "../utils/validator";
import { getValidateIndividualDelivery } from "../utils/amend_validator";
import { PackageCountPerDealField } from "../components/PackageCountPerDeal";
import { UnassignedDeliveryContentTable } from "../components/UnassignedDeliveryContentTable";
import { preventSendNull } from "../utils/api";
import { useWholesalerAddresses } from "../hooks/useWholesalerAddresses";
import { IndividualDelivery } from "../api/individual_deliveries";
import { dateProps, dateTimeProps } from "../utils/locale";
import { HacobellPriceField } from "../components/HacobellPrice";
import { MemoField } from "../components/MemoField";
import { useLoginInfo } from "../hooks/useLoginInfo";
import { usePrefectures } from "../hooks/usePrefectures";
import { ReceiptTimingField } from "../components/ReceiptTimingField";
import { IntegerNumberInput } from "../components/IntegerNumberInput";
import { HiddenInput } from "../components/hidden";
import { useSpotContractDeal } from "../hooks/useDeal";
import { commonStyles } from "../components/common_styles";
import { IndexLabelSimpleFormIterator } from "../components/IndexLabelSimpleFormIterator";
import { getSpotContractIdField } from "../utils/deal";
import { useFormContext, useWatch } from "react-hook-form";
import { HandoverAtField } from "../components/HandoverAtField";
import { DeleteDeliveryButton, EditDeliveryButton, InspectDeliveryButton } from "../components/SpotContractAmendButton";
import { MobileDeliveryActionButtons } from "../components/MobileDeliveryActionButtons";
import { individualDeliveryFormStyles } from "../components/IndividualDeliveryFormStyles";
import { EmptyTitle } from "../components/EmptyTitle";

const initialValues = {
  attachments: [],
  delivery_address_id: undefined,
  delivery_contents: [],
  hacobell: {
    arrival_preferred_from: "",
    arrival_preferred_to: "",
    preferred_vehicle: "ten_ton",
    options: [],
  },
  self_delivery: {
    shipping_company_name: "",
    handover_date: "",
    handover_time: "",
    driver_name: "",
    driver_phone_number: "",
    car_number: "",
  },
};

const DeliveryActionButtons = ({ canUpdateDelivery }: { canUpdateDelivery?: boolean }) => (
  <MobileDeliveryActionButtons canUpdateDelivery={canUpdateDelivery}>
    <EditDeliveryButton showLabel />
    <DeleteDeliveryButton showLabel />
    <InspectDeliveryButton showLabel />
  </MobileDeliveryActionButtons>
);

export const SpotContractIndividualDeliveryShow = (props: {
  record?: IndividualDelivery;
  canUpdateDelivery?: boolean;
}) => {
  const { canUpdateDelivery, ...showProps } = props;
  const { record } = useShowController(showProps);
  const translate = useTranslate();
  if (!record) {
    return null;
  }
  const { delivery_type } = record;
  const isHacobell = delivery_type === "hacobell";
  const isSelfDelivery = delivery_type === "self_delivery";
  return (
    <Show {...showProps} title={<EmptyTitle />} resource="spot_contract_individual_deliveries" actions={false}>
      <SimpleShowLayout>
        <DeliveryActionButtons canUpdateDelivery={canUpdateDelivery} />
        <SelectField source="status" choices={createDeliveryStatusChoices()} />
        <SelectField source="has_producer_reviewed" choices={createHasProducerReviewed()} />
        <ArrayField source="delivery_contents">
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false} empty={null}>
              <TextField source="grade.name" emptyText={translate("ui.value.default_grade")} />
              <NumberField source="package_count" />
            </Datagrid>
          </Box>
        </ArrayField>
        <ReferenceField source="delivery_address_id" reference="wholesaler_addresses" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <SelectField source="delivery_type" choices={createDeliveryTypeChoices()} />
        {isHacobell && <SelectField source="hacobell.status" choices={createHacobellStatuses()} />}
        {isHacobell && <DateField source="hacobell.arrival_preferred_from" {...dateTimeProps} />}
        {isHacobell && <DateField source="hacobell.arrival_preferred_to" {...dateTimeProps} />}
        {isHacobell && (
          <SelectField source="hacobell.preferred_vehicle" choices={createHacobellPreferredVehicleChoices()} />
        )}
        {isHacobell && (
          <HandoverAtField label="resources.spot_contract_individual_deliveries.fields.hacobell.handover_timing" />
        )}
        {isHacobell && <ReceiptTimingField source="receipt_timing" />}
        {isHacobell && <TextField source="hacobell.customer_name" />}
        {isHacobell && <TextField source="hacobell.shipping_company_name" />}
        {isHacobell && <TextField source="hacobell.shipping_company_phone_number" />}
        {isHacobell && <TextField source="hacobell.driver_name" />}
        {isHacobell && <TextField source="hacobell.driver_phone_number" />}
        {isHacobell && <MemoField source="hacobell.shipment_notes" />}
        {isSelfDelivery && <DateField source="self_delivery.handover_date" {...dateProps} />}
        {isSelfDelivery && <TextField source="self_delivery.handover_time" />}
        {isSelfDelivery && <ReceiptTimingField source="receipt_timing" />}
        {isSelfDelivery && <TextField source="self_delivery.shipping_company_name" />}
        {isSelfDelivery && <TextField source="self_delivery.driver_name" />}
        {isSelfDelivery && <TextField source="self_delivery.driver_phone_number" />}
        {isSelfDelivery && <TextField source="self_delivery.car_number" />}
        <TextField source="after_delivery_inspection_memo" component="pre" />
        <ArrayField source="after_delivery_inspection_attachments">
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false} empty={null}>
              <AttachmentFileField />
            </Datagrid>
          </Box>
        </ArrayField>
      </SimpleShowLayout>
    </Show>
  );
};

const redirect = (record: any) => {
  return () => {
    return `spot_contract_deals/${record.amend_id}/show/delivery`;
  };
};

type Choice = {
  id: number;
  name: string;
};

function createGradeChoices(defaultGrade: Grade, inspectionResultGrades: Grade[] | undefined): Choice[] {
  if (inspectionResultGrades === undefined) {
    return [];
  }
  return inspectionResultGrades.map((hc) => {
    if (hc === null) {
      // 等級差額なしの場合
      return {
        // 仕様上はnullがidになるのを期待しているが、nullを設定すると未選択になるので保存時にnullに置き換える
        id: defaultGrade.id,
        name: "ui.value.default_grade",
      };
    }
    return {
      id: hc.id,
      name: hc.name,
    };
  });
}

// HACK: parseに関数を渡すとformの値が日付型がstringになる
const parseDateTime = (value: any) => value;

const defaultHacobellValues = {
  arrival_preferred_from: null,
  arrival_preferred_to: null,
  preferred_vehicle: "ten_ton",
  options: [],
};

const getTransform = (inspectionResults: InspectionResult[], grade: Grade) => {
  const hasNonGradeInspectionResult = !!inspectionResults.find((inspectionResult) => inspectionResult.grade === null);

  return (record: any) => {
    const self_delivery = record.delivery_type === "self_delivery" ? record.self_delivery : {};
    const deliveryContents = hasNonGradeInspectionResult
      ? record.delivery_contents?.map((dc: any) => {
          return {
            grade_id: grade.id === dc.grade_id ? null : dc.grade_id,
            package_count: dc.package_count,
          };
        })
      : record.delivery_contents;

    return {
      ...record,
      delivery_address_id: record.delivery_address_id ?? null,
      delivery_contents: deliveryContents ?? [],
      hacobell:
        record.delivery_type === "hacobell"
          ? {
              ...defaultHacobellValues,
              ...record.hacobell,
            }
          : null,
      self_delivery:
        record.delivery_type === "self_delivery"
          ? {
              handover_date: self_delivery.handover_date ? self_delivery.handover_date : null,
              handover_time: self_delivery.handover_time ? self_delivery.handover_time : "",
              driver_name: self_delivery.driver_name ? self_delivery.driver_name : "",
              driver_phone_number: self_delivery.driver_phone_number ? self_delivery.driver_phone_number : "",
              car_number: self_delivery.car_number ? self_delivery.car_number : "",
              shipping_company_name: self_delivery.shipping_company_name ? self_delivery.shipping_company_name : "",
            }
          : null,
    };
  };
};

export const SpotContractIndividualDeliveryCreate = (props: any) => {
  const createProps = useCreateController(props);
  const { record } = createProps;
  const [loadingDeal, deal] = useSpotContractDeal(record?.amend_id);
  const [loadingAmend, amend] = useSpotContractAmend(record?.amend_id);
  const [loadingPrefectures, prefectures] = usePrefectures();
  const [loadingWholesalerAddresses, wholesalerAddresses] = useWholesalerAddresses();
  const [loadingUnassignedDeliveryContents, unassignedDeliveryContents] = useSpotContractUnassignedDeliveryContents(
    record?.amend_id
  );
  const [loadingLoginInfo, loginInfo] = useLoginInfo();
  const loading =
    loadingDeal ||
    loadingAmend ||
    loadingUnassignedDeliveryContents ||
    loadingWholesalerAddresses ||
    loadingLoginInfo ||
    loadingPrefectures;
  const isSubmitActionRef = useRef(false);

  if (
    !record ||
    loading ||
    deal === null ||
    amend === null ||
    loginInfo === null ||
    unassignedDeliveryContents === null
  ) {
    return null;
  }
  const defaultAddress = wholesalerAddresses.find((wa) => wa.is_default);
  const defaultAddressId = defaultAddress?.id;
  const { decision_date } = deal;
  const { grade, handover_address, inspection_results, packaging_form } = amend;
  const transform = getTransform(inspection_results, grade); // ボタンでもtransformをしているので、Toolbar経由で渡す
  const inspectionResultGrades = inspection_results.map((inspectionResult) => inspectionResult.grade);
  const gradeChoices = createGradeChoices(
    grade,
    inspection_results?.map((hc) => hc.grade)
  );
  const formatGrade = (v: any) => {
    return v === null ? grade.id : v;
  };
  const isTruck = packaging_form.shipping_method === "truck";
  const deliveryTypeChoices = createDeliveryTypeChoices().filter((item) => {
    if (isTruck) {
      return true;
    }
    // トラックじゃない場合はハコベルは選択不可
    return item.id !== "hacobell";
  });
  const defaultDeliveryType = isTruck ? "hacobell" : "self_delivery";

  const validateIndividualDelivery = getValidateIndividualDelivery(
    unassignedDeliveryContents,
    grade.id,
    isSubmitActionRef,
    decision_date,
    false
  );
  return (
    <Create {...props} redirect={redirect(record)} mutationMode="pessimistic">
      <SimpleForm
        sx={individualDeliveryFormStyles}
        toolbar={
          <IndividualDeliveryToolbar
            disabledSaveAsDraft={false}
            saveAsNotifyDriverInfo={false}
            handoverAddress={handover_address}
            prefectures={prefectures}
            wholesalerAddresses={wholesalerAddresses}
            formTransform={transform}
            isSubmitActionRef={isSubmitActionRef}
          />
        }
        mode="onTouched"
        defaultValues={initialValues}
        validate={validateIndividualDelivery}
      >
        <Labeled>
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <FunctionField source="id" render={getSpotContractIdField} />
          </ReferenceField>
        </Labeled>

        <Labeled label="resources.spot_contract_amends.fields.producer">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="producer.company_name" />
          </ReferenceField>
        </Labeled>
        <Labeled label="resources.spot_contract_amends.fields.brand">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="brand.name" />
          </ReferenceField>
        </Labeled>
        <Labeled label="resources.spot_contract_amends.fields.grade">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="grade.name" />
          </ReferenceField>
        </Labeled>
        <Labeled label="resources.spot_contract_amends.fields.prefecture">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="prefecture.name" />
          </ReferenceField>
        </Labeled>
        <Labeled label="resources.spot_contract_amends.fields.region">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="region.name" />
          </ReferenceField>
        </Labeled>
        <Labeled label="resources.spot_contract_amends.fields.packaging_form">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="packaging_form.name" />
          </ReferenceField>
        </Labeled>
        <Labeled>
          <ReferenceField source="amend_id" reference="spot_contract_amends" label="1口当たりの数量" link={false}>
            <PackageCountPerDealField />
          </ReferenceField>
        </Labeled>

        <UnassignedDeliveryContentTable
          unassignedDeliveryContents={unassignedDeliveryContents}
          inspectionResultGrades={inspectionResultGrades}
        />

        <ArrayInput source="delivery_contents">
          <IndexLabelSimpleFormIterator>
            <SelectInput source="grade_id" choices={gradeChoices} format={formatGrade} defaultValue={grade.id} />
            <IntegerNumberInput source="package_count" />
          </IndexLabelSimpleFormIterator>
        </ArrayInput>

        <h3>運送手配内容</h3>
        <ReferenceInput source="delivery_address_id" reference="wholesaler_addresses">
          <SelectInput optionText="name" defaultValue={defaultAddressId} />
        </ReferenceInput>
        <SelectInput source="delivery_type" choices={deliveryTypeChoices} defaultValue={defaultDeliveryType} />

        <Labeled>
          <ReferenceField
            source="amend_id"
            reference="spot_contract_deals"
            label="resources.spot_contract_individual_deliveries.fields.decision_date"
            link={false}
            addLabel={true}
          >
            <DateField source="decision_date" />
          </ReferenceField>
        </Labeled>

        <DelivererForm deliverer="hacobell">
          <TextInput source="hacobell.customer_name" defaultValue={loginInfo.wholesaler.name} />
          <DateTimeInput source="hacobell.arrival_preferred_from" parse={parseDateTime} />
          <DateTimeInput source="hacobell.arrival_preferred_to" parse={parseDateTime} />
          <SelectInput source="hacobell.preferred_vehicle" choices={createHacobellPreferredVehicleChoices()} />
          <HacobellPriceField packagingForm={amend.packaging_form} handoverAddress={amend.handover_address} />
          <TextInput source="hacobell.shipment_notes" parse={preventSendNull} fullWidth multiline />
        </DelivererForm>

        <DelivererForm deliverer="self_delivery">
          <DateInput source="self_delivery.handover_date" />
          <TextInput source="self_delivery.handover_time" />
          <TextInput source="self_delivery.shipping_company_name" />
          <TextInput source="self_delivery.driver_name" />
          <TextInput source="self_delivery.driver_phone_number" />
          <TextInput source="self_delivery.car_number" />
        </DelivererForm>
      </SimpleForm>
    </Create>
  );
};

export const SpotContractIndividualDeliveryEdit = (props: any) => {
  const { record }: { record: IndividualDelivery } = useEditController();
  const [loadingDeal, deal] = useSpotContractDeal(record?.amend_id);
  const [loadingAmend, amend] = useSpotContractAmend(record?.amend_id);
  const [loadingPrefectures, prefectures] = usePrefectures();
  const [loadingWholesalerAddresses, wholesalerAddresses] = useWholesalerAddresses();
  const [loadingUnassignedDeliveryContents, unassignedDeliveryContents] = useSpotContractUnassignedDeliveryContents(
    record?.amend_id
  );
  const loading =
    loadingDeal ||
    loadingAmend ||
    loadingUnassignedDeliveryContents ||
    loadingPrefectures ||
    loadingWholesalerAddresses;
  const isSubmitActionRef = useRef(false);

  if (!record || loading || deal === null || amend === null || unassignedDeliveryContents === null) {
    return null;
  }
  const { status } = record;
  const isDraft = status === "draft";
  const isPublished = status === "waiting_for_inspection";
  const { decision_date } = deal;
  const { grade, handover_address, inspection_results, packaging_form } = amend;
  const transform = getTransform(inspection_results, grade); // ボタンでもtransformをしているので、Toolbar経由で渡す
  const inspectionResultGrades = inspection_results.map((inspectionResult) => inspectionResult.grade);
  const gradeChoices = createGradeChoices(
    grade,
    inspection_results?.map((hc) => hc.grade)
  );
  const formatGrade = (v: any) => {
    return v === null ? grade.id : v;
  };
  const isTruck = packaging_form.shipping_method === "truck";
  const deliveryTypeChoices = createDeliveryTypeChoices().filter((item) => {
    if (isTruck) {
      return true;
    }
    // トラックじゃない場合はハコベルは選択不可
    return item.id !== "hacobell";
  });

  const validateIndividualDelivery = getValidateIndividualDelivery(
    unassignedDeliveryContents,
    grade.id,
    isSubmitActionRef,
    decision_date,
    isPublished
  );

  return (
    <Edit {...props} undoable={false} redirect={redirect(record)} mutationMode="pessimistic">
      <SimpleForm
        sx={individualDeliveryFormStyles}
        toolbar={
          <IndividualDeliveryToolbar
            disabledSaveAsDraft={!isDraft}
            saveAsNotifyDriverInfo={isPublished}
            handoverAddress={handover_address}
            prefectures={prefectures}
            wholesalerAddresses={wholesalerAddresses}
            formTransform={transform}
            isSubmitActionRef={isSubmitActionRef}
          />
        }
        validate={validateIndividualDelivery}
        mode="onTouched"
      >
        <HiddenInput source="status" />
        <Labeled>
          <TextField source="id" />
        </Labeled>
        <Labeled>
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <FunctionField source="id" render={getSpotContractIdField} />
          </ReferenceField>
        </Labeled>

        <Labeled label="resources.spot_contract_amends.fields.producer">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="producer.company_name" />
          </ReferenceField>
        </Labeled>
        <Labeled label="resources.spot_contract_amends.fields.brand">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="brand.name" />
          </ReferenceField>
        </Labeled>
        <Labeled label="resources.spot_contract_amends.fields.grade">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="grade.name" />
          </ReferenceField>
        </Labeled>
        <Labeled label="resources.spot_contract_amends.fields.prefecture">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="prefecture.name" />
          </ReferenceField>
        </Labeled>
        <Labeled label="resources.spot_contract_amends.fields.region">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="region.name" />
          </ReferenceField>
        </Labeled>
        <Labeled label="resources.spot_contract_amends.fields.packaging_form">
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="packaging_form.name" />
          </ReferenceField>
        </Labeled>
        <Labeled>
          <ReferenceField source="amend_id" reference="spot_contract_amends" label="1口当たりの数量" link={false}>
            <PackageCountPerDealField />
          </ReferenceField>
        </Labeled>

        <UnassignedDeliveryContentTable
          unassignedDeliveryContents={unassignedDeliveryContents}
          inspectionResultGrades={inspectionResultGrades}
        />

        <ArrayInput source="delivery_contents">
          <IndexLabelSimpleFormIterator disableAdd={!isDraft} disableRemove={!isDraft}>
            <SelectInput
              source="grade_id"
              choices={gradeChoices}
              format={formatGrade}
              defaultValue={grade.id}
              readOnly={!isDraft}
            />
            <IntegerNumberInput source="package_count" readOnly={!isDraft} />
          </IndexLabelSimpleFormIterator>
        </ArrayInput>

        <h3>運送手配内容</h3>
        <ReferenceInput source="delivery_address_id" reference="wholesaler_addresses">
          <SelectInput optionText="name" readOnly={!isDraft} />
        </ReferenceInput>
        <SelectInput source="delivery_type" choices={deliveryTypeChoices} readOnly={!isDraft} />

        <Labeled>
          <ReferenceField
            source="amend_id"
            reference="spot_contract_deals"
            label="resources.spot_contract_individual_deliveries.fields.decision_date"
            link={false}
            addLabel={true}
          >
            <DateField source="decision_date" />
          </ReferenceField>
        </Labeled>

        <DelivererForm deliverer="hacobell">
          <TextInput source="hacobell.customer_name" />
          <DateTimeInput source="hacobell.arrival_preferred_from" parse={parseDateTime} />
          <DateTimeInput source="hacobell.arrival_preferred_to" parse={parseDateTime} />
          <SelectInput
            source="hacobell.preferred_vehicle"
            choices={createHacobellPreferredVehicleChoices()}
            defaultValue="ten_ton"
          />
          <HacobellPriceField packagingForm={amend.packaging_form} handoverAddress={amend.handover_address} />
          <TextInput source="hacobell.shipment_notes" parse={preventSendNull} fullWidth multiline />
        </DelivererForm>

        <DelivererForm deliverer="self_delivery">
          <DateInput source="self_delivery.handover_date" readOnly={!isDraft} />
          <TextInput source="self_delivery.handover_time" />
          <TextInput source="self_delivery.shipping_company_name" />
          <TextInput source="self_delivery.driver_name" />
          <TextInput source="self_delivery.driver_phone_number" />
          <TextInput source="self_delivery.car_number" />
        </DelivererForm>
      </SimpleForm>
    </Edit>
  );
};

const validateAfterDeliveryInspection = (_: any, values: any) => {
  if (values.has_trouble && !(values.after_delivery_inspection_memo.length > 0)) {
    return "validation.after_delivery_inspections.should_fill_reason";
  }
  return null;
};

const validateMemo = [maxLength(2048), validateAfterDeliveryInspection];

const HasTroubleWatcher = () => {
  const hasTrouble = useWatch({ name: "has_trouble" });
  const memo = useWatch({ name: "after_delivery_inspection_memo" });
  const formContext = useFormContext();

  useEffect(() => {
    if (formContext && !(memo.length > 0)) {
      formContext.setValue("after_delivery_inspection_memo", memo, { shouldValidate: true, shouldTouch: true });
    }
  }, [hasTrouble, memo]);

  return null;
};

const sanitizeFileAttachments = (record: any) => {
  const attachments = record.after_delivery_inspection_attachments ?? [];
  const sanitized =
    attachments.length > 0 && attachments[attachments.length - 1] === "" ? attachments.slice(0, -1) : attachments;

  return { ...record, after_delivery_inspection_attachments: sanitized };
};

export const SpotContractAfterDeliveryInspectionEdit = (props: any) => {
  const { record }: { record: IndividualDelivery } = useEditController();
  const transform = (record: any) => {
    if (record.has_trouble) {
      return {
        ...sanitizeFileAttachments(record),
        is_exception_flow: true,
      };
    }
    return {
      ...sanitizeFileAttachments(record),
      is_exception_flow: false,
      status: "done",
    };
  };

  return (
    <Edit {...props} undoable={false} transform={transform} redirect={redirect(record)} mutationMode="pessimistic">
      <SimpleForm sx={individualDeliveryFormStyles} toolbar={<UndeletableToolbar />} mode="onTouched">
        <Labeled>
          <TextField source="id" />
        </Labeled>
        <Labeled>
          <ReferenceField source="amend_id" reference="spot_contract_amends" link={false}>
            <TextField source="id" />
          </ReferenceField>
        </Labeled>
        <HasTroubleWatcher />
        <SelectInput source="has_trouble" choices={createIsExceptionFlow()} validate={validateRequired} />
        <TextInput
          source="after_delivery_inspection_memo"
          parse={preventSendNull}
          validate={validateMemo}
          fullWidth
          multiline
        />
        <ArrayInput source="after_delivery_inspection_attachments">
          <IndexLabelSimpleFormIterator>
            <AttachmentFileInput label={false} />
          </IndexLabelSimpleFormIterator>
        </ArrayInput>
      </SimpleForm>
    </Edit>
  );
};
